/**
 * Multi-Entity Consolidation View
 * 
 * Global context bar and entity leaderboards for corporate groups.
 * Provides consolidated view of financial exposure across all legal entities.
 * 
 * Features:
 * - Global context bar with group-level exposure summary
 * - Entity leaderboard ranked by exposure
 * - Drill-down to entity-specific exposure breakdown
 * - Framework coverage comparison across entities
 */

import React, { useState, useEffect, useCallback } from 'react';
import { 

  Building2, Globe2, TrendingUp, TrendingDown, AlertTriangle,
  ChevronRight, ChevronDown, RefreshCw, Filter, BarChart3,
  DollarSign, Shield, Target, Users, MapPin, Layers, ArrowUpRight,
  ArrowDownRight, Minus, Eye, ExternalLink
} from 'lucide-react';
import { apiFetch } from '../services/api_service';

// Entity risk tier colors
const RISK_TIER_CONFIG = {
  CRITICAL: { label: 'Critical', color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: AlertTriangle },
  HIGH: { label: 'High', color: 'bg-orange-500/20 text-orange-400 border-orange-500/30', icon: TrendingUp },
  MEDIUM: { label: 'Medium', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', icon: Minus },
  LOW: { label: 'Low', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', icon: TrendingDown }
};

// Sample corporate group structure
const CORPORATE_GROUP = {
  id: 'grp001',
  name: 'Demo Corp International',
  headquarters: 'Frankfurt, Germany',
  entities: [
    {
      id: 'entity_001',
      name: 'Demo Corp GmbH',
      country: 'DE',
      jurisdiction: 'EU',
      type: 'Headquarters',
      employees: 2500,
      exposure: { expected: 3250000, p90: 7800000 },
      frameworks: ['CSDDD', 'CSRD', 'LkSG'],
      obligationCount: 24,
      overdueCount: 3,
      breachedCount: 1,
      riskTier: 'HIGH'
    },
    {
      id: 'entity_002',
      name: 'Demo Corp France SAS',
      country: 'FR',
      jurisdiction: 'EU',
      type: 'Subsidiary',
      employees: 850,
      exposure: { expected: 1450000, p90: 3200000 },
      frameworks: ['CSDDD', 'CSRD'],
      obligationCount: 18,
      overdueCount: 1,
      breachedCount: 0,
      riskTier: 'MEDIUM'
    },
    {
      id: 'entity_003',
      name: 'Demo Corp UK Ltd',
      country: 'GB',
      jurisdiction: 'UK',
      type: 'Subsidiary',
      employees: 620,
      exposure: { expected: 980000, p90: 2100000 },
      frameworks: ['UK_MS', 'CSRD'],
      obligationCount: 12,
      overdueCount: 0,
      breachedCount: 0,
      riskTier: 'LOW'
    },
    {
      id: 'entity_004',
      name: 'Demo Corp Asia Pacific',
      country: 'SG',
      jurisdiction: 'APAC',
      type: 'Regional HQ',
      employees: 340,
      exposure: { expected: 520000, p90: 1100000 },
      frameworks: ['CSDDD'],
      obligationCount: 8,
      overdueCount: 2,
      breachedCount: 0,
      riskTier: 'MEDIUM'
    },
    {
      id: 'entity_005',
      name: 'Demo Corp Americas Inc',
      country: 'US',
      jurisdiction: 'US',
      type: 'Subsidiary',
      employees: 480,
      exposure: { expected: 750000, p90: 1600000 },
      frameworks: ['CSDDD', 'CSRD'],
      obligationCount: 10,
      overdueCount: 1,
      breachedCount: 0,
      riskTier: 'MEDIUM'
    }
  ]
};

const MultiEntityConsolidation = () => {
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [sortField, setSortField] = useState('exposure');
  const [sortDir, setSortDir] = useState('desc');
  const [filterJurisdiction, setFilterJurisdiction] = useState('ALL');
  const [loading, setLoading] = useState(false);
  const [monteCarloResult, setMonteCarloResult] = useState(null);

  // Calculate group totals
  const groupTotals = CORPORATE_GROUP.entities.reduce((acc, entity) => ({
    totalExposure: acc.totalExposure + entity.exposure.expected,
    totalP90: acc.totalP90 + entity.exposure.p90,
    totalObligations: acc.totalObligations + entity.obligationCount,
    totalOverdue: acc.totalOverdue + entity.overdueCount,
    totalBreached: acc.totalBreached + entity.breachedCount,
    totalEmployees: acc.totalEmployees + entity.employees
  }), { totalExposure: 0, totalP90: 0, totalObligations: 0, totalOverdue: 0, totalBreached: 0, totalEmployees: 0 });

  // Run Monte Carlo for consolidated view
  const runGroupMonteCarlo = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiFetch(`/spine/v1/fin/readmodels/monte-carlo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          scope_type: 'GROUP',
          scope_id: CORPORATE_GROUP.id,
          group_id: CORPORATE_GROUP.id,
          period: 'FY2024',
          num_simulations: 10000
        })
      });
      if (response && !response._error) {
        setMonteCarloResult(response);
      } else {
        // Fallback to calculated values if API fails
        setMonteCarloResult({
          expected_loss: groupTotals.totalExposure,
          p50: groupTotals.totalExposure * 1.3,
          p90: groupTotals.totalP90,
          p95: groupTotals.totalP90 * 1.15,
          min_exposure: groupTotals.totalExposure * 0.6,
          max_exposure: groupTotals.totalP90 * 1.5,
          num_simulations: 10000
        });
      }
    } catch (err) {
      console.error('Error running Monte Carlo:', err);
      // Set fallback values on error
      setMonteCarloResult({
        expected_loss: groupTotals.totalExposure,
        p50: groupTotals.totalExposure * 1.3,
        p90: groupTotals.totalP90,
        p95: groupTotals.totalP90 * 1.15,
        min_exposure: groupTotals.totalExposure * 0.6,
        max_exposure: groupTotals.totalP90 * 1.5,
        num_simulations: 10000
      });
    } finally {
      setLoading(false);
    }
  }, [groupTotals]);

  useEffect(() => {
    runGroupMonteCarlo();
  }, [runGroupMonteCarlo]);

  // Sort entities
  const sortedEntities = [...CORPORATE_GROUP.entities]
    .filter(e => filterJurisdiction === 'ALL' || e.jurisdiction === filterJurisdiction)
    .sort((a, b) => {
      let aVal, bVal;
      switch (sortField) {
        case 'exposure':
          aVal = a.exposure.expected;
          bVal = b.exposure.expected;
          break;
        case 'obligations':
          aVal = a.obligationCount;
          bVal = b.obligationCount;
          break;
        case 'overdue':
          aVal = a.overdueCount;
          bVal = b.overdueCount;
          break;
        case 'name':
          aVal = a.name;
          bVal = b.name;
          break;
        default:
          aVal = a.exposure.expected;
          bVal = b.exposure.expected;
      }
      if (typeof aVal === 'string') {
        return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
    });

  // Unique jurisdictions for filter
  const jurisdictions = ['ALL', ...new Set(CORPORATE_GROUP.entities.map(e => e.jurisdiction))];

  // Format currency
  const formatCurrency = (value) => {
    if (value >= 1000000) {
      return `€${(value / 1000000).toFixed(1)}M`;
    }
    return `€${(value / 1000).toFixed(0)}K`;
  };

  // Get trend indicator
  const getTrendIndicator = (entity) => {
    if (entity.breachedCount > 0) return { icon: TrendingUp, color: 'text-red-400', label: 'Critical' };
    if (entity.overdueCount > 2) return { icon: TrendingUp, color: 'text-orange-400', label: 'Increasing' };
    if (entity.overdueCount === 0) return { icon: TrendingDown, color: 'text-emerald-400', label: 'Stable' };
    return { icon: Minus, color: 'text-yellow-400', label: 'Moderate' };
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white p-6" data-testid="multi-entity-consolidation">
      {/* Global Context Bar */}
      <div className="bg-gradient-to-r from-indigo-900/30 via-purple-900/20 to-indigo-900/30 rounded-xl border border-indigo-500/20 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-indigo-500/20 rounded-xl">
              <Globe2 className="w-8 h-8 text-indigo-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">{CORPORATE_GROUP.name}</h1>
              <p className="text-sm text-indigo-300/70 flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                {CORPORATE_GROUP.headquarters} • {CORPORATE_GROUP.entities.length} Legal Entities • {groupTotals.totalEmployees.toLocaleString()} Employees
              </p>
            </div>
          </div>
          
          <button 
            onClick={runGroupMonteCarlo}
            disabled={loading}
            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm flex items-center gap-2 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Run Monte Carlo
          </button>
        </div>

        {/* Group KPIs */}
        <div className="grid grid-cols-6 gap-4">
          <div className="p-4 bg-[#0A0A0A]/50 rounded-lg border border-indigo-500/10">
            <div className="flex items-center gap-2 text-indigo-300/70 text-xs mb-1">
              <DollarSign className="w-4 h-4" />
              Total Exposure (EL)
            </div>
            <div className="text-2xl font-bold text-white">{formatCurrency(groupTotals.totalExposure)}</div>
            {monteCarloResult && (
              <div className="text-xs text-indigo-300/50 mt-1">
                MC: {formatCurrency(monteCarloResult.expected_loss)}
              </div>
            )}
          </div>
          
          <div className="p-4 bg-[#0A0A0A]/50 rounded-lg border border-indigo-500/10">
            <div className="flex items-center gap-2 text-indigo-300/70 text-xs mb-1">
              <BarChart3 className="w-4 h-4" />
              P90 Exposure
            </div>
            <div className="text-2xl font-bold text-white">{formatCurrency(groupTotals.totalP90)}</div>
            {monteCarloResult && (
              <div className="text-xs text-indigo-300/50 mt-1">
                MC P90: {formatCurrency(monteCarloResult.p90)}
              </div>
            )}
          </div>
          
          <div className="p-4 bg-[#0A0A0A]/50 rounded-lg border border-indigo-500/10">
            <div className="flex items-center gap-2 text-indigo-300/70 text-xs mb-1">
              <Target className="w-4 h-4" />
              Total Obligations
            </div>
            <div className="text-2xl font-bold text-white">{groupTotals.totalObligations}</div>
          </div>
          
          <div className="p-4 bg-[#0A0A0A]/50 rounded-lg border border-orange-500/10">
            <div className="flex items-center gap-2 text-orange-300/70 text-xs mb-1">
              <AlertTriangle className="w-4 h-4" />
              Overdue
            </div>
            <div className="text-2xl font-bold text-orange-400">{groupTotals.totalOverdue}</div>
          </div>
          
          <div className="p-4 bg-[#0A0A0A]/50 rounded-lg border border-red-500/10">
            <div className="flex items-center gap-2 text-red-300/70 text-xs mb-1">
              <Shield className="w-4 h-4" />
              Breached
            </div>
            <div className="text-2xl font-bold text-red-400">{groupTotals.totalBreached}</div>
          </div>
          
          <div className="p-4 bg-[#0A0A0A]/50 rounded-lg border border-indigo-500/10">
            <div className="flex items-center gap-2 text-indigo-300/70 text-xs mb-1">
              <Building2 className="w-4 h-4" />
              Entities
            </div>
            <div className="text-2xl font-bold text-white">{CORPORATE_GROUP.entities.length}</div>
          </div>
        </div>
      </div>

      {/* Filters & Controls */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Layers className="w-5 h-5 text-purple-400" />
          Entity Leaderboard
        </h2>
        
        <div className="flex items-center gap-3">
          <select
            value={filterJurisdiction}
            onChange={(e) => setFilterJurisdiction(e.target.value)}
            className="px-3 py-2 bg-[#1F1F1F] border border-[#2F2F2F] rounded-lg text-sm text-white"
          >
            {jurisdictions.map(j => (
              <option key={j} value={j}>{j === 'ALL' ? 'All Jurisdictions' : j}</option>
            ))}
          </select>
          
          <select
            value={sortField}
            onChange={(e) => setSortField(e.target.value)}
            className="px-3 py-2 bg-[#1F1F1F] border border-[#2F2F2F] rounded-lg text-sm text-white"
          >
            <option value="exposure">Sort by Exposure</option>
            <option value="obligations">Sort by Obligations</option>
            <option value="overdue">Sort by Overdue</option>
            <option value="name">Sort by Name</option>
          </select>
          
          <button
            onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')}
            className="p-2 bg-[#1F1F1F] border border-[#2F2F2F] rounded-lg hover:bg-[#2F2F2F]"
          >
            {sortDir === 'desc' ? <ArrowDownRight className="w-4 h-4" /> : <ArrowUpRight className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Entity Leaderboard */}
      <div className="space-y-3">
        {sortedEntities.map((entity, index) => {
          const trend = getTrendIndicator(entity);
          const riskConfig = RISK_TIER_CONFIG[entity.riskTier] || RISK_TIER_CONFIG.MEDIUM;
          const exposurePct = (entity.exposure.expected / groupTotals.totalExposure * 100).toFixed(1);
          
          return (
            <div 
              key={entity.id}
              className={`p-4 bg-[#141414] rounded-xl border transition-all cursor-pointer ${
                selectedEntity === entity.id 
                  ? 'border-purple-500/50 bg-purple-500/5' 
                  : 'border-[#1F1F1F] hover:border-[#2F2F2F]'
              }`}
              onClick={() => setSelectedEntity(selectedEntity === entity.id ? null : entity.id)}
              data-testid={`entity-row-${entity.id}`}
            >
              <div className="flex items-center justify-between">
                {/* Entity Info */}
                <div className="flex items-center gap-4">
                  <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center text-purple-400 font-bold">
                    #{index + 1}
                  </div>
                  
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium text-white">{entity.name}</h3>
                      <span className={`px-2 py-0.5 text-xs rounded border ${riskConfig.color}`}>
                        {riskConfig.label}
                      </span>
                      <span className="px-2 py-0.5 text-xs bg-[#1F1F1F] text-[#6B6B6B] rounded">
                        {entity.type}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-[#6B6B6B] mt-1">
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {entity.country}
                      </span>
                      <span>•</span>
                      <span>{entity.jurisdiction}</span>
                      <span>•</span>
                      <span>{entity.employees.toLocaleString()} employees</span>
                      <span>•</span>
                      <span className="flex items-center gap-1">
                        {entity.frameworks.map(f => (
                          <span key={f} className="px-1.5 py-0.5 bg-[#2F2F2F] rounded text-[10px]">{f}</span>
                        ))}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Metrics */}
                <div className="flex items-center gap-6">
                  <div className="text-right">
                    <div className="text-xs text-[#6B6B6B]">Exposure (EL)</div>
                    <div className="text-lg font-bold text-white">{formatCurrency(entity.exposure.expected)}</div>
                    <div className="text-xs text-[#6B6B6B]">{exposurePct}% of group</div>
                  </div>
                  
                  <div className="text-right">
                    <div className="text-xs text-[#6B6B6B]">P90</div>
                    <div className="text-lg font-medium text-[#A0A0A0]">{formatCurrency(entity.exposure.p90)}</div>
                  </div>
                  
                  <div className="text-center w-16">
                    <div className="text-xs text-[#6B6B6B]">Obligations</div>
                    <div className="text-lg font-medium text-white">{entity.obligationCount}</div>
                  </div>
                  
                  <div className="text-center w-16">
                    <div className="text-xs text-[#6B6B6B]">Overdue</div>
                    <div className={`text-lg font-medium ${entity.overdueCount > 0 ? 'text-orange-400' : 'text-emerald-400'}`}>
                      {entity.overdueCount}
                    </div>
                  </div>
                  
                  <div className="text-center w-16">
                    <div className="text-xs text-[#6B6B6B]">Breached</div>
                    <div className={`text-lg font-medium ${entity.breachedCount > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                      {entity.breachedCount}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <trend.icon className={`w-4 h-4 ${trend.color}`} />
                    <ChevronRight className={`w-4 h-4 text-[#6B6B6B] transition-transform ${selectedEntity === entity.id ? 'rotate-90' : ''}`} />
                  </div>
                </div>
              </div>

              {/* Expanded Detail */}
              {selectedEntity === entity.id && (
                <div className="mt-4 pt-4 border-t border-[#1F1F1F]">
                  <div className="grid grid-cols-4 gap-4">
                    {/* Exposure Bar */}
                    <div className="col-span-2">
                      <div className="text-xs text-[#6B6B6B] mb-2">Exposure Distribution</div>
                      <div className="h-4 bg-[#1F1F1F] rounded-full overflow-hidden relative">
                        <div 
                          className="h-full bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full"
                          style={{ width: `${exposurePct}%` }}
                        />
                        <div className="absolute inset-0 flex items-center justify-center text-xs text-white font-medium">
                          {exposurePct}% of Group Exposure
                        </div>
                      </div>
                    </div>
                    
                    {/* Quick Actions */}
                    <div className="col-span-2 flex items-center justify-end gap-2">
                      <button className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded text-xs flex items-center gap-1">
                        <Eye className="w-3 h-3" />
                        View Obligations
                      </button>
                      <button className="px-3 py-1.5 bg-[#1F1F1F] hover:bg-[#2F2F2F] text-white rounded text-xs flex items-center gap-1">
                        <ExternalLink className="w-3 h-3" />
                        Drill Down
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Monte Carlo Results Panel */}
      {monteCarloResult && (
        <div className="mt-6 p-4 bg-[#141414] rounded-xl border border-[#1F1F1F]">
          <h3 className="text-sm font-semibold text-purple-400 mb-3 flex items-center gap-2">
            <BarChart3 className="w-4 h-4" />
            Monte Carlo Simulation Results ({monteCarloResult.num_simulations.toLocaleString()} simulations)
          </h3>
          <div className="grid grid-cols-6 gap-4 text-center">
            <div>
              <div className="text-xs text-[#6B6B6B]">Expected Loss</div>
              <div className="text-lg font-bold">{formatCurrency(monteCarloResult.expected_loss)}</div>
            </div>
            <div>
              <div className="text-xs text-[#6B6B6B]">P50 (Median)</div>
              <div className="text-lg font-bold">{formatCurrency(monteCarloResult.p50)}</div>
            </div>
            <div>
              <div className="text-xs text-[#6B6B6B]">P90</div>
              <div className="text-lg font-bold text-orange-400">{formatCurrency(monteCarloResult.p90)}</div>
            </div>
            <div>
              <div className="text-xs text-[#6B6B6B]">P95</div>
              <div className="text-lg font-bold text-red-400">{formatCurrency(monteCarloResult.p95)}</div>
            </div>
            <div>
              <div className="text-xs text-[#6B6B6B]">Min</div>
              <div className="text-lg font-medium text-emerald-400">{formatCurrency(monteCarloResult.min_exposure)}</div>
            </div>
            <div>
              <div className="text-xs text-[#6B6B6B]">Max</div>
              <div className="text-lg font-medium text-red-400">{formatCurrency(monteCarloResult.max_exposure)}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiEntityConsolidation;
